import React, { useState, useEffect, useRef } from 'react';
import downloadDetailIcon from '../images/download-detail.png';
import informationIcon from '../images/information.svg';
import fallbackLogo from '../images/recorb-app.svg';
import { useNavigate, useParams } from 'react-router-dom';
import { appApi } from '../utils/api';
import { Modal } from 'antd';

// Types copied from RecorbPage
type FormState = {
  downloadVersion: string;
  agentName: string;
  agencyEmail: string;
  downloadInfoEmail: string;
  serialNumbersInput: string;
}

type ValidationErrors = {
  [key in keyof FormState]?: string;
}

// Session utils copied from RecorbPage
const getSessionData = () => {
  try {
    const agentName = sessionStorage.getItem('agentName') || '';
    const agencyEmail = sessionStorage.getItem('agencyEmail') || '';
    return { agentName, agencyEmail };
  } catch (error) {
    return { agentName: '', agencyEmail: '' };
  }
};

// Public app type
type PublicApp = {
  id: number;
  name: string;
  description: string;
  appInfo?: string | null;
  logoUrl: string;
  latestVersion?: string | null;
};

type PublicVersion = {
  id: number;
  version: string;
  changes: string;
  releaseDate?: string;
};

const MAX_SERIAL_NUMBER_LINES = 100;

const AppDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState<PublicApp | null>(null);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [versions, setVersions] = useState<PublicVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<PublicVersion | null>(null);

  // Fetch public app detail
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await appApi.get(`/api/app/apps/${id}`);
        if (!mounted) return;
        setApp(res.data);
      } catch (e) {
        // ignore and keep fallback
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  // Fetch versions for this app
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await appApi.get(`/api/app/apps/${id}/versions`);
        if (!mounted) return;
        const items = (res.data?.items ?? []) as PublicVersion[];
        setVersions(items);
        if (items.length > 0) {
          setSelectedVersion(items[0]);
        }
      } catch (e) {
        setVersions([]);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  // Form state
  const [formState, setFormState] = useState<FormState>(() => {
    const sessionData = getSessionData();
    return {
      downloadVersion: '1.0.0',
      agentName: sessionData.agentName,
      agencyEmail: sessionData.agencyEmail,
      downloadInfoEmail: '',
      serialNumbersInput: ''
    };
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // Update initial version from app.latestVersion if present
  useEffect(() => {
    if (selectedVersion?.version) {
      setFormState((prev) => ({ ...prev, downloadVersion: selectedVersion.version }));
    } else if (app?.latestVersion) {
      setFormState((prev) => ({ ...prev, downloadVersion: app.latestVersion! }));
    }
  }, [selectedVersion?.version, app?.latestVersion]);

  // helpers copied from RecorbPage
  const getEmailOptions = () => {
    const options = [] as { value: string; label: string }[];
    if (formState.agencyEmail) options.push({ value: formState.agencyEmail, label: formState.agencyEmail });
    return options;
  };

  useEffect(() => {
    const sessionData = getSessionData();
    if (sessionData.agencyEmail && formState.agencyEmail !== sessionData.agencyEmail) {
      setFormState(prev => ({ ...prev, agencyEmail: sessionData.agencyEmail }));
    }
    if (sessionData.agentName && formState.agentName !== sessionData.agentName) {
      setFormState(prev => ({ ...prev, agentName: sessionData.agentName }));
    }
    const emailOptions = getEmailOptions();
    if (emailOptions.length > 0 && !formState.downloadInfoEmail) {
      setFormState(prev => ({ ...prev, downloadInfoEmail: emailOptions[0].value }));
    }
  }, [formState, formState.agencyEmail]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const parseSerialNumbersByLine = (input: string): string[] => {
    return input
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  };

  const validateForm = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};
    if (!formState.downloadVersion) newErrors.downloadVersion = 'Download version is required';
    if (!formState.agentName) newErrors.agentName = 'Agent name is required';
    if (!formState.agencyEmail) newErrors.agencyEmail = 'Agency email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.agencyEmail)) newErrors.agencyEmail = 'Please enter a valid email address';
    if (!formState.downloadInfoEmail) newErrors.downloadInfoEmail = 'Download info email is required';
    const serialNumbers = parseSerialNumbersByLine(formState.serialNumbersInput);
    if (serialNumbers.length === 0) newErrors.serialNumbersInput = 'Please enter serial numbers (one per line)';
    else if (serialNumbers.length > MAX_SERIAL_NUMBER_LINES) {
      newErrors.serialNumbersInput = `Maximum ${MAX_SERIAL_NUMBER_LINES} serial numbers are allowed`;
    }
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const serialNumbers = parseSerialNumbersByLine(formState.serialNumbersInput);

      if (serialNumbers.length === 0) {
        setErrors(prev => ({ ...prev, serialNumbersInput: 'Please enter serial numbers (one per line)' }));
        return;
      }

      if (serialNumbers.length > MAX_SERIAL_NUMBER_LINES) {
        setErrors(prev => ({
          ...prev,
          serialNumbersInput: `Maximum ${MAX_SERIAL_NUMBER_LINES} serial numbers are allowed`
        }));
        return;
      }

      const configData = {
        SerialNumbers: serialNumbers,
        AgentName: formState.agentName,
        AgencyEmail: formState.agencyEmail,
        NotificationEmail: formState.downloadInfoEmail
      };
      // include app id and selected version for server-side packaging
      const response = await appApi.post(`/api/app/download`, {
        appId: Number(id),
        version: formState.downloadVersion,
        config: configData,
      });
      if (response.status === 200) {
        setShowSuccessPopup(true);
      } else {
        setSubmitError('Failed to process your request. Please try again later.');
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const featureItems = selectedVersion ? [selectedVersion.changes] : [app?.description || ''];

  return (
    <div className="recorb-page px-6 py-8 max-w-4xl mx-auto">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        {!logoLoaded && <div className="h-[53px] w-[190px] bg-gray-200 animate-pulse rounded-md"></div>}
        <img
          src={app?.logoUrl || fallbackLogo}
          alt={app?.name || 'App Logo'}
          className={`h-[53px] w-[190px] object-contain ${!logoLoaded ? 'invisible absolute' : ''}`}
          onLoad={() => setLogoLoaded(true)}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = fallbackLogo as unknown as string;
          }}
        />
      </div>

      {/* Application Information */}
      <div>
        <div className="py-2 px-4">
          <h3 className="font-semibold inline-block bg-[#0077C8] text-white py-1 px-3">Application Information</h3>
        </div>
        <div className="py-4 px-6">
          <p className="text-sm leading-relaxed break-words">
            {app?.appInfo || app?.description || 'Application information will be shown here.'}
          </p>
        </div>
      </div>
      <hr className="my-6" />

      {/* Version */}
      <div className="py-2 px-4 mb-6">
        <p className="font-semibold inline-block bg-[#0077C8] text-white py-1 px-3 border border-[#DADEE3]">
          Version {formState.downloadVersion}
        </p>
      </div>

      {/* Feature List (uses selected version changes) */}
      <div className="mb-6">
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 list-disc pl-8">
          {featureItems.map((item, index) => (
            <li key={index} className="text-sm">{item}</li>
          ))}
        </ul>
      </div>

      {/* Form Heading */}
      <div className="mb-4 flex items-center">
        <img src={informationIcon} alt="Information" className="h-5 w-5 mr-2" />
        <h4 className="font-bold text-lg">Please enter the following information to download the app.</h4>
      </div>

      {/* Error */}
      {submitError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {submitError}
        </div>
      )}

      {/* Form (copied from RecorbPage) */}
      <form ref={formRef} onSubmit={handleSubmit} className="bg-[#F4F6F9] border border-[#BCC2FF] rounded-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Download Version */}
          <div className="form-group">
            <label htmlFor="downloadVersion" className="block text-sm font-medium mb-1">Download Version <span className="text-red-600 align-middle leading-none">※</span></label>
            <select
              id="downloadVersion"
              name="downloadVersion"
              value={formState.downloadVersion}
              onChange={(e) => {
                const ver = e.target.value;
                const found = versions.find(v => v.version === ver) || null;
                setSelectedVersion(found);
                setFormState(prev => ({ ...prev, downloadVersion: ver }));
              }}
              required
              className={`w-full h-11 border ${errors.downloadVersion ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 leading-6 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              {versions.length > 0 ? (
                versions.map((v) => (
                  <option key={v.id} value={v.version}>Version {v.version}</option>
                ))
              ) : (
                <option value={formState.downloadVersion}>Version {formState.downloadVersion}</option>
              )}
            </select>
            {errors.downloadVersion && (
              <p className="text-red-500 text-xs mt-1">{errors.downloadVersion}</p>
            )}
          </div>
          {/* Agent Name */}
          <div className="form-group">
            <label htmlFor="agentName" className="block text-sm font-medium mb-1">Distributor Name<span className="text-red-600 align-middle leading-none">※</span></label>
            <input
              type="text"
              id="agentName"
              name="agentName"
              value={formState.agentName}
              onChange={handleChange}
              required
              disabled
              className={`w-full h-11 border ${errors.agentName ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 leading-6 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.agentName && (
              <p className="text-red-500 text-xs mt-1">{errors.agentName}</p>
            )}
          </div>

          {/* Agency contact email address */}
          <div className="form-group">
            <label htmlFor="agencyEmail" className="block text-sm font-medium mb-1">Distributor contact email address<span className="text-red-600 align-middle leading-none">※</span></label>
            <input
              type="email"
              id="agencyEmail"
              name="agencyEmail"
              value={formState.agencyEmail}
              onChange={handleChange}
              required
              disabled
              className={`w-full h-11 border ${errors.agencyEmail ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 leading-6 focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.agencyEmail && (
              <p className="text-red-500 text-xs mt-1">{errors.agencyEmail}</p>
            )}
          </div>

          {/* Serial Numbers */}
          <div className="form-group md:col-span-2">
            <label htmlFor="serialNumbersInput" className="block text-sm font-medium mb-1">Serial Numbers (Camera Serial or TSC1 Serial Numbers)<span className="text-red-600 align-middle leading-none">※</span></label>
            <p className="text-xs text-gray-600 mb-1">List of serial numbers, one per line.</p>
            <textarea
              maxLength={255}
              id="serialNumbersInput"
              name="serialNumbersInput"
              value={formState.serialNumbersInput}
              onChange={(e) => {
                const { name, value } = e.target;
                setFormState(prev => ({ ...prev, [name]: value }));
              }}
              required
              rows={4}
              className={`w-full border ${errors.serialNumbersInput ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 leading-6`}
            />
            {errors.serialNumbersInput && (
              <p className="text-red-500 text-xs mt-1">{errors.serialNumbersInput}</p>
            )}
          </div>
        </div>
      </form>

      {/* Download Button */}
      <div className="flex justify-center col-span-1 md:col-span-2 mt-4">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="border-0 bg-transparent p-0 relative"
        >
          {isSubmitting && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
          <img
            src={downloadDetailIcon}
            alt={`Download ${app?.name || 'App'}`}
            className={`h-auto max-h-[60px] hover:opacity-80 transition-opacity ${isSubmitting ? 'opacity-70' : ''}`}
          />
        </button>
      </div>

      {/* Success Popup */}
      <Modal
        title="Success"
        open={showSuccessPopup}
        onOk={() => navigate('/')}
        onCancel={() => navigate('/')}
        okText="Close"
        cancelButtonProps={{ style: { display: 'none' } }}
        className="custom-modal-version"
      >
        <p className="text-gray-700">We have received your request.</p>
        <p className="text-gray-700">A download link will be sent to you shortly from software.support@takagi-j.com.</p>
        <p className="text-gray-700">Please check your inbox in a few minutes.</p>
      </Modal>
    </div>
  );
};

export default AppDetailPage;
